import {AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {FileDashboardSummary} from '../../../api';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faArrowDownToBracket, faFile, faFolderOpen} from '@fortawesome/pro-solid-svg-icons';
import {TooltipDirective} from '@buildinginformationcloud/bic-ui-components';
import {SkeletonLoadingDirective} from '../../../directives/skeleton-loading.directive';
import {EmptyStateComponent} from '../../util/design/empty-state/empty-state.component';

export interface FileTypeDisplay {
  label: string;
  colorClass: string;
  bgColorClass: string;
  count: number;
  size: number;
}

@Component({
  selector: 'app-dashboard-files',
  templateUrl: './dashboard-files.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FaIconComponent, SkeletonLoadingDirective, TooltipDirective, EmptyStateComponent]
})
export class DashboardFilesComponent implements AfterViewInit, OnDestroy {

  @Input() fileSummary: FileDashboardSummary | null = null;
  @Input() loading = false;

  @ViewChild('statsRow', {static: false}) statsRow!: ElementRef<HTMLElement>;

  compactStats = false;

  private resizeObserver?: ResizeObserver;

  constructor(private el: ElementRef, private zone: NgZone) {
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.zone.run(() => this.checkStatsOverflow());
    });
    this.resizeObserver.observe(this.el.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private checkStatsOverflow(): void {
    const row = this.statsRow?.nativeElement;
    if (!row) return;

    const boxes = row.querySelectorAll('.stat-box') as NodeListOf<HTMLElement>;
    if (boxes.length === 0) return;

    // Check if the narrowest box is too small for label text
    let minWidth = Infinity;
    for (let i = 0; i < boxes.length; i++) {
      minWidth = Math.min(minWidth, boxes[i].clientWidth);
    }
    // "Gesamtgröße" needs ~90px, switch to compact when boxes are too narrow
    this.compactStats = minWidth < 90;
  }

  /** Max number of file types shown before grouping the rest as "Andere" */
  private readonly MAX_VISIBLE_TYPES = 3;
  protected readonly faArrowDownToBracket = faArrowDownToBracket;
  protected readonly faFile = faFile;
  protected readonly faFolderOpen = faFolderOpen;

  private fileTypeLabels: Record<string, string> = {
    'IFC': 'IFC Modelle',
    'PDF': 'PDF Dokumente',
    'EXCEL': 'Excel Dateien',
    'WORD': 'Word Dokumente',
    'POWERPOINT': 'Powerpoint',
    'COORDINATION': 'Koordinationsmodelle',
    'UNKNOWN': 'Sonstige',
  };

  // Farbskala von dunkel nach hell (für 6 verschiedene Abstufungen)
  private readonly colorScale = [
    '#042E3F', // Dunkelste
    '#365865',
    '#68828C',
    '#9BABB2',
    '#B8C2C7',
    '#CDD5D9', // Hellste
  ];

  get totalFiles(): number {
    return this.fileSummary?.totalFiles ?? 0;
  }

  get totalFolders(): number {
    return this.fileSummary?.totalFolders ?? 0;
  }

  get totalSize(): number {
    return this.fileSummary?.totalSize ?? 0;
  }

  get formattedSize(): string {
    const bytes = this.totalSize;
    if (bytes === 0) return '0 B';
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb >= 1) return gb.toFixed(1) + ' GB';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return mb.toFixed(1) + ' MB';
    const kb = bytes / 1024;
    return kb.toFixed(1) + ' KB';
  }

  get hasFiles(): boolean {
    return this.totalFiles > 0;
  }

  get fileTypeDisplays(): FileTypeDisplay[] {
    if (!this.fileSummary?.fileTypes) return [];

    const sortedTypes = this.fileSummary.fileTypes
      .filter(ft => ft.fileType !== 'FOLDER' && (ft.count ?? 0) > 0)
      .sort((a, b) => (b.size ?? 0) - (a.size ?? 0));

    return sortedTypes.map((ft, index) => {
      const label = this.fileTypeLabels[ft.fileType ?? 'UNKNOWN'] ?? 'Sonstige';
      const color = this.colorScale[Math.min(index, this.colorScale.length - 1)];

      return {
        label,
        colorClass: '', // Nicht mehr verwendet, wir nutzen inline styles
        bgColorClass: color,
        count: ft.count ?? 0,
        size: ft.size ?? 0
      };
    });
  }

  get allFileTypesForBar(): FileTypeDisplay[] {
    // Gleiche Logik wie fileTypeDisplays
    return this.fileTypeDisplays;
  }

  /**
   * Returns a limited list of file types for the legend.
   * If there are more than MAX_VISIBLE_TYPES, the rest are grouped as "Andere".
   */
  get visibleFileTypeDisplays(): FileTypeDisplay[] {
    const all = this.fileTypeDisplays;
    if (all.length <= this.MAX_VISIBLE_TYPES) {
      return all;
    }

    const visible = all.slice(0, this.MAX_VISIBLE_TYPES);
    const rest = all.slice(this.MAX_VISIBLE_TYPES);

    const andereCount = rest.reduce((sum, ft) => sum + ft.count, 0);
    const andereSize = rest.reduce((sum, ft) => sum + ft.size, 0);

    const lastColor = this.colorScale[Math.min(this.MAX_VISIBLE_TYPES, this.colorScale.length - 1)];

    visible.push({
      label: 'Andere',
      colorClass: '',
      bgColorClass: lastColor,
      count: andereCount,
      size: andereSize,
    });

    return visible;
  }

  getBarWidth(ft: FileTypeDisplay): string {
    if (this.totalSize === 0) return '0%';
    return ((ft.size / this.totalSize) * 100).toFixed(1) + '%';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb >= 1) return gb.toFixed(1) + ' GB';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return mb.toFixed(1) + ' MB';
    const kb = bytes / 1024;
    return kb.toFixed(1) + ' KB';
  }
}

